import { type RefObject, useEffect, useRef } from "react";
import type { GridApi } from "ag-grid-community";

/**
 * A grid inside a resizable panel is mounted before the panel hands it a width, so AG Grid
 * lays the columns out against zero and never revisits it: every column keeps its default
 * 200px and the grid scrolls sideways. Refitting on every observed resize covers both that
 * first paint and the user dragging the split.
 */
export const useFitColumnsToPanel = (
	containerRef: RefObject<HTMLElement | null>,
	apiRef: RefObject<GridApi | null>,
) => {
	useEffect(() => {
		const element = containerRef.current;
		if (!element) return;

		const fit = () => {
			const api = apiRef.current;
			if (api && !api.isDestroyed()) api.sizeColumnsToFit();
		};

		const observer = new ResizeObserver(fit);
		observer.observe(element);
		return () => observer.disconnect();
	}, [containerRef, apiRef]);
};

export const useGridPanelFit = () => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const apiRef = useRef<GridApi | null>(null);
	useFitColumnsToPanel(containerRef, apiRef);

	// The observer fires once when it starts watching, which happens before the grid hands
	// over its api, and the panel does not resize again afterwards. Without this the first
	// fit never runs and every column keeps its default width.
	const registerApi = (api: GridApi) => {
		apiRef.current = api;
		api.sizeColumnsToFit();
	};

	return { containerRef, apiRef, registerApi };
};
